import React, { useEffect, useState } from 'react';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import Select from 'react-select';
import ExportButton from '../components/ExportButton';
import PermissionButton from '../components/PermissionButton';
import { useGetUniversitiesQuery, useGetCountriesQuery, useAddUniversityMutation, useUpdateUniversityMutation, useDeleteUniversityMutation, useGetCurrenciesQuery, useGetEducationLevelsQuery, useAddCountryMutation } from '../store/api';
import UniversityEditModal from '../components/modals/UniversityEditModal';
import UniversityViewModal from '../components/modals/UniversityViewModal';
import { toast } from 'react-toastify';
import { Audio } from 'react-loader-spinner'
import TableHeader from '../components/TableHeader';
import { usePermission } from '../hooks/useAuth';

export default function Universities() {
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState(null);
  const [viewingUniversity, setViewingUniversity] = useState(null);
  const [activeTab, setActiveTab] = useState('details');
  
  // Country creation modal states
  const [showCountryModal, setShowCountryModal] = useState(false);
  const [newCountryData, setNewCountryData] = useState({ name: '', code: '', is_active: 1 });
  const [countrySearchValue, setCountrySearchValue] = useState('');
  
  // Permission hooks - strictly permission-based
  const canAddUniversity = usePermission('university.add');
  const canEditUniversity = usePermission('university.edit');
  const canDeleteUniversity = usePermission('university.delete');
  const canViewUniversity = usePermission('university.view'); // Already being used in route protection
  
  const [formData, setFormData] = useState({ 
    name: '', 
    country_id: '', 
    city_id: '', 
    ranking: '', 
    representatives: '',
    focal_point: '', 
    phone: '',
    email: '', 
    website: '', 
    address: '',
    required_documents: '',
    degree: [], 
    submission_deadline: '', 
    average_cost: '', 
    comments: '',
    is_active: 1, 
    programs: [],
    scholarships: []
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUniversityCount, setTotalUniversityCount] = useState(0);
  const [universitiesData, setUniversitiesData] = useState(null);
  const [isExport, setIsExport] = useState(0);
  const [exportData, setExportData] = useState([]);

  // Get Universities Query
  const { data, isError, error, isSuccess, isLoading, refetch } = useGetUniversitiesQuery({
    keyword: searchTerm,
    country_id: countryFilter,
    is_active: statusFilter,
    is_export: isExport,
    page: currentPage,
    limit: itemsPerPage
  })

  // Get Countries for filter
  const { data: countriesData, refetch: refetchCountries } = useGetCountriesQuery({
    keyword: '',
    is_active: '1',
    is_export: 0,
    page: 1,
    limit: 1000
  });

  // Get Currencies for university programs
  const { data: currenciesData } = useGetCurrenciesQuery();

  // Get Education Levels for university programs
  const { data: educationLevelsData } = useGetEducationLevelsQuery();

  useEffect(() => {
    if (isExport == 1) {
      const transformedData = data.data.map(university => ({
        'Universitet Adı': university.name,
        'Ölkə': university.country?.name || '',
        'Şəhər': university.city?.name || '',
        'Reytinq': university.ranking,
        'Nümayəndələr': university.representatives || '',
        'Ortalama Xərc': university.average_cost,
        'Son Tarix': university.submission_deadline,
        'Əlaqəli Şəxs': university.focal_point,
        'Email': university.email,
        'Telefon': university.phone || '',
        'Veb Sayt': university.website || '',
        'Ünvan': university.address || '',
        'Tələb Olunan Sənədlər': university.required_documents || '',
        'Əlavə Şərhlər': university.comments || '',
        'Status': university.is_active ? 'Aktiv' : 'Deaktiv',
        'ID': university.id
      }));

      setExportData(transformedData)
    } else {
      if (!isLoading && data) {
        setUniversitiesData(data.data.data)
        setTotalPages(Math.ceil(data.data.total / data.data.per_page))
        setTotalUniversityCount(data.data.total)
      }
    }
  }, [data, isSuccess, isError, isLoading, error])

  // Add University Mutation
  const [addUniversity, {
    data: createResponse,
    isError: createIsError,
    error: createError,
    isSuccess: createIsSuccess,
    isLoading: createIsLoading
  }] = useAddUniversityMutation()

  useEffect(() => {
    if (!createIsLoading && createResponse) {
      toast.success(createResponse.message);
      setIsModalOpen(false);
      setFormData({ 
        name: '', 
        country_id: '', 
        city_id: '', 
        ranking: '', 
        representatives: '',
        focal_point: '', 
        phone: '',
        email: '', 
        website: '', 
        address: '',
        required_documents: '',
        degree: [], 
        submission_deadline: '', 
        average_cost: '', 
        comments: '',
        is_active: 1, 
        programs: [],
        scholarships: []
      });
      refetch(); // Data'yı yenile
    }

    if (createIsError) {
      toast.error(createResponse.message);
    }
  }, [createResponse, createIsSuccess, createIsError, createIsLoading, createError, refetch])

  // Update University Mutation
  const [updateUniversity, {
    data: updateData,
    isError: updateIsError,
    error: updateError,
    isSuccess: updateIsSuccess,
    isLoading: updateIsLoading
  }] = useUpdateUniversityMutation()

  useEffect(() => {
    if (!updateIsLoading && updateData) {
      toast.success(updateData.message)
      setIsModalOpen(false);
      setEditingUniversity(null);
      setFormData({ 
        name: '', 
        country_id: '', 
        city_id: '', 
        ranking: '', 
        representatives: '',
        focal_point: '', 
        phone: '',
        email: '', 
        website: '', 
        address: '',
        required_documents: '',
        degree: [], 
        submission_deadline: '', 
        average_cost: '', 
        comments: '',
        is_active: 1, 
        programs: [],
        scholarships: []
      });
      refetch(); // Data'yı yenile
    }

    if (updateIsError) {
      toast.error("Yeniləmə uğursuz oldu")
      console.log(updateError);
    }
  }, [updateData, updateIsSuccess, updateIsError, updateIsLoading, updateError, refetch])

  // Delete University Mutation
  const [deleteUniversity,{
    data: deleteRes,
    isError: deleteIsError,
    error: deleteError,
    isSuccess: deleteIsSuccess,
    isLoading: deleteIsLoading
  }] = useDeleteUniversityMutation() 

  // Add Country Mutation
  const [addCountry, {
    data: countryCreateResponse,
    isError: countryCreateIsError,
    error: countryCreateError,
    isSuccess: countryCreateIsSuccess,
    isLoading: countryCreateIsLoading
  }] = useAddCountryMutation();

  useEffect(() => {
    if(!deleteIsLoading && deleteRes && deleteIsSuccess){
      toast.success(deleteRes.message);
      refetch(); // Data'yı yenile
    }
    
    if(deleteIsError && deleteError){
      toast.error("Silmə əməliyyatı uğursuz oldu");
    }
  }, [deleteIsSuccess, deleteIsError, deleteRes, deleteError, deleteIsLoading, refetch])

  // Country create useEffect
  useEffect(() => {
    if (!countryCreateIsLoading && countryCreateResponse) {
      toast.success(countryCreateResponse.message);
      setShowCountryModal(false);
      setNewCountryData({ name: '', code: '', is_active: 1 });
      // Refresh countries list and select the new country
      refetchCountries().then((result) => {
        if (result.data) {
          const newCountry = result.data.data.data.find(country => country.name === countryCreateResponse.data.name);
          if (newCountry) {
            setCountryFilter(newCountry.id.toString());
          }
        }
      });
    }

    if (countryCreateIsError) {
      toast.error(countryCreateError?.data?.message || 'Ölkə əlavə etmə uğursuz oldu');
    }
  }, [countryCreateResponse, countryCreateIsSuccess, countryCreateIsError, countryCreateIsLoading, countryCreateError, refetchCountries])

  // Prepare country options for React Select
  const countryOptions = countriesData?.data?.data?.map(country => ({
    value: country.id.toString(),
    label: country.name
  })) || [];

  // Pagination calculations
  const startIndex = (currentPage - 1) * itemsPerPage;

  const exportHeaders = [
    'Universitet Adı', 'Ölkə', 'Şəhər', 'Reytinq', 'Nümayəndələr', 'Ortalama Xərc', 'Son Tarix',
    'Əlaqəli Şəxs', 'Email', 'Telefon', 'Veb Sayt', 'Ünvan', 'Tələb Olunan Sənədlər', 'Əlavə Şərhlər', 'Status', 'ID'
  ];

  const handleAdd = () => {
    setEditingUniversity(null);
    setFormData({
      name: '',
      country_id: '',
      city_id: '',
      ranking: '',
      representatives: '',
      focal_point: '',
      phone: '',
      email: '',
      website: '',
      address: '',
      required_documents: '',
      degree: [],
      submission_deadline: '',
      average_cost: '',
      comments: '',
      is_active: 1,
      programs: [],
      scholarships: []
    });
    setIsModalOpen(true);
  };

  const handleEdit = (university) => {
    setEditingUniversity(university);
    
    // Map programs to frontend structure
    const mappedPrograms = university.programs ? university.programs.map(program => ({
      name: program.name || '',
      degree: program.degree ? (typeof program.degree === 'string' ? program.degree.split(',').map(d => d.trim()) : program.degree) : [],
      duration: program.duration || '',
      tuition_amount: program.tuition_amount || program.tuitionAmount || '',
      currency_id: program.currency_id || program.currencyId || '',
      description: program.description || '',
      is_active: program.is_active !== undefined ? program.is_active : (program.isActive !== undefined ? program.isActive : true)
    })) : [];

    // Map scholarships to frontend structure
    const mappedScholarships = university.scholarships ? university.scholarships.map(scholarship => ({
      name: scholarship.name || '',
      description: scholarship.description || '',
      degree_level: scholarship.degree_level ? (typeof scholarship.degree_level === 'string' ? scholarship.degree_level.split(',').map(d => d.trim()) : scholarship.degree_level) : [],
      field_of_study: scholarship.field_of_study || '',
      amount: scholarship.amount || '',
      currency_id: scholarship.currency_id || scholarship.currencyId || '',
      amount_type: scholarship.amount_type || '',
      coverage_type: scholarship.coverage_type || '',
      application_deadline: scholarship.application_deadline ? 
        (scholarship.application_deadline.includes('T') 
          ? scholarship.application_deadline.split('T')[0] 
          : scholarship.application_deadline.match(/^\d{4}-\d{2}-\d{2}$/) 
            ? scholarship.application_deadline 
            : '') 
        : '',
      requirements: scholarship.requirements || '',
      application_process: scholarship.application_process || '',
      contact_email: scholarship.contact_email || '',
      website: scholarship.website || '',
      additional_benefits: scholarship.additional_benefits || '',
      duration: scholarship.duration || '',
      is_active: scholarship.is_active !== undefined ? scholarship.is_active : (scholarship.isActive !== undefined ? scholarship.isActive : true)
    })) : [];

    setFormData({
      name: university.name,
      country_id: university.country_id,
      city_id: university.city_id,
      ranking: university.ranking ? university.ranking.toString() : '',
      representatives: university.representatives || '',
      focal_point: university.focal_point || '',
      phone: university.phone || '',
      email: university.email || '',
      website: university.website || '',
      address: university.address || '',
      required_documents: university.required_documents || '',
      degree: university.degree ? university.degree.split(', ') : [],
      submission_deadline: university.submission_deadline || '',
      average_cost: university.average_cost || university.avarage_cost || '',
      comments: university.comments || '',
      is_active: university.is_active || 1,
      programs: mappedPrograms,
      scholarships: mappedScholarships
    });
    setIsModalOpen(true);
  };

  const handleView = (university) => {
    setViewingUniversity(university);
    setActiveTab('details');
    setIsViewModalOpen(true);
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setCountryFilter('');
    setStatusFilter('');
    setCurrentPage(1);
  };

  // Country handlers
  const handleCreateCountry = () => {
    setNewCountryData({ name: countrySearchValue, code: '', is_active: 1 });
    setShowCountryModal(true);
  };

  const handleCountrySubmit = (e) => {
    e.preventDefault();
    addCountry(newCountryData);
  };

  const handleCancelCountryModal = () => {
    setShowCountryModal(false);
    setNewCountryData({ name: '', code: '', is_active: 1 });
  };

  const handleDelete = (universityId) => {
    if (window.confirm('Bu universiteti silmək istədiyinizə əminsiniz?')) {
      console.log('Deleting university:', universityId);
      deleteUniversity(universityId)
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Process programs data before sending
    const processedPrograms = formData.programs ? formData.programs.map(program => ({
      ...program,
      degree: Array.isArray(program.degree) ? program.degree.join(',') : program.degree,
      currency_id: program.currency_id ? parseInt(program.currency_id) : null,
      tuition_amount: program.tuition_amount ? parseFloat(program.tuition_amount) : null,
      is_active: program.is_active ? 1 : 0
    })) : [];

    // Process scholarships data before sending
    const processedScholarships = formData.scholarships ? formData.scholarships.map(scholarship => ({
      ...scholarship,
      degree_level: Array.isArray(scholarship.degree_level) ? scholarship.degree_level.join(',') : scholarship.degree_level,
      amount: parseFloat(scholarship.amount) || null,
      currency_id: parseInt(scholarship.currency_id) || null,
      is_active: scholarship.is_active ? 1 : 0
    })) : [];

    // Convert degree array to string before sending
    const submitData = {
      ...formData,
      degree: Array.isArray(formData.degree) ? formData.degree.join(', ') : formData.degree,
      programs: processedPrograms,
      scholarships: processedScholarships
    };

    if(editingUniversity){
      updateUniversity({id: editingUniversity?.id, obj: submitData})
    }else{
      addUniversity(submitData);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Universitetlər</h1>
          <p className="mt-1 text-sm text-gray-600">
            Tərəfdaş universitetləri idarə edin
          </p>
          <div className="mt-2 flex items-center space-x-4">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800 border border-blue-200">
              Ümumi: {totalUniversityCount}
            </span>
            {(searchTerm || countryFilter || statusFilter) && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800 border border-green-200">
                Tapılan: {totalUniversityCount}
              </span>
            )}
          </div>
        </div>
        <div className="mt-4 sm:mt-0 sm:flex sm:space-x-3">
          <ExportButton
            setIsExport={setIsExport}
            data={exportData}
            filename="universitetler"
            headers={exportHeaders}
          />
          {canAddUniversity && (
            <button
              onClick={handleAdd}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <PlusIcon className="-ml-1 mr-2 h-5 w-5" />
              Yeni Universitet
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 max-w-5xl">
        <div className="md:col-span-2">
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Universitet, şəhər, ölkə ilə axtarış..."
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <div>
          <Select
            value={countryFilter ? 
              countryOptions.find(option => option.value === countryFilter.toString()) : null
            }
            onChange={(selectedOption) => {
              setCountryFilter(selectedOption?.value || '');
            }}
            onInputChange={(inputValue) => {
              setCountrySearchValue(inputValue);
            }}
            options={countryOptions}
            placeholder="Ölkə axtarın və ya seçin..."
            isSearchable={true}
            isClearable={true}
            noOptionsMessage={({ inputValue }) => (
              <div className="p-2">
                <div className="text-gray-500 mb-2">"{inputValue}" tapılmadı</div>
                <button
                  type="button"
                  onClick={handleCreateCountry}
                  className="w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded border border-blue-200"
                >
                  + "{inputValue}" ölkəsini yarat
                </button>
              </div>
            )}
            styles={{
              control: (provided, state) => ({
                ...provided,
                borderColor: state.isFocused ? '#3b82f6' : '#d1d5db',
                boxShadow: state.isFocused ? '0 0 0 1px #3b82f6' : 'none',
                '&:hover': {
                  borderColor: state.isFocused ? '#3b82f6' : '#9ca3af'
                }
              }),
              option: (provided, state) => ({
                ...provided,
                backgroundColor: state.isSelected 
                  ? '#3b82f6' 
                  : state.isFocused 
                  ? '#dbeafe' 
                  : 'white',
                color: state.isSelected ? 'white' : '#374151',
                '&:hover': {
                  backgroundColor: state.isSelected ? '#3b82f6' : '#dbeafe'
                }
              }),
              noOptionsMessage: (provided) => ({
                ...provided,
                padding: 0,
              })
            }}
            className="react-select-container"
            classNamePrefix="react-select"
          />
        </div>
        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Bütün statuslar</option>
            <option value="1">Yalnız aktiv</option>
            <option value="0">Yalnız deaktiv</option>
          </select>
        </div>
        <div>
          <button
            onClick={handleClearFilters}
            disabled={!searchTerm && !countryFilter && !statusFilter}
            className="w-full px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Filtri təmizlə
          </button>
        </div>
      </div>

      <TableHeader
        currentPage={currentPage}
        setItemsPerPage={setItemsPerPage}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        startIndex={startIndex}
        title={"Universitetlər"}
        totalPages={totalPages}
        totalRecordCount={totalUniversityCount}
      />

      {/* Universities table */}
      <div className="bg-white shadow-lg overflow-hidden sm:rounded-xl border border-blue-100">
        <div className="px-6 py-6">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gradient-to-r from-blue-50 to-blue-100">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Universitet</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Yer</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Focal Point</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    <div className="flex items-center space-x-2">
                      <span>Status</span>
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    </div>
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-semibold text-gray-700 uppercase tracking-wider border-b-2 border-blue-300">
                    Əməliyyatlar
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {!isLoading && universitiesData?.length > 0 && universitiesData.map((university, index) => (
                  <tr key={university.id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-blue-100 transition-all duration-200 group">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className={`flex-shrink-0 h-3 w-3 rounded-full mr-3 transition-colors ${
                          university.is_active ? 'bg-blue-600 group-hover:bg-blue-700' : 'bg-gray-400 group-hover:bg-gray-500'
                        }`}></div>
                        <div>
                          <div className={`text-sm font-semibold transition-colors ${
                            university.is_active ? 'text-gray-900 group-hover:text-blue-900' : 'text-gray-500 group-hover:text-gray-600'
                          }`}>
                            {university.name}
                          </div>
                          <div className="text-xs text-gray-500">#{university.ranking} Reytinq</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{university.city ? university.city.name : ""}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 max-w-48 truncate" title={university.focal_point}>
                        {university.focal_point}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        university.is_active 
                          ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                          : 'bg-red-100 text-red-800 border border-red-200'
                      }`}>
                        {university.is_active ? 'Aktiv' : 'Deaktiv'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <div className="flex justify-end space-x-2">
                        {canViewUniversity && (
                          <button
                            onClick={() => handleView(university)}
                            className="inline-flex items-center p-2 bg-green-100 text-green-600 hover:bg-green-200 hover:text-green-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Ətraflı bax"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canEditUniversity && (
                          <button
                            onClick={() => handleEdit(university)}
                            className="inline-flex items-center p-2 bg-blue-100 text-blue-600 hover:bg-blue-200 hover:text-blue-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Redaktə et"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                        {canDeleteUniversity && (
                          <button
                            onClick={() => handleDelete(university.id)}
                            className="inline-flex items-center p-2 bg-red-100 text-red-600 hover:bg-red-200 hover:text-red-800 rounded-lg transition-all duration-200 hover:scale-105"
                            title="Sil"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!isLoading && universitiesData?.length === 0 && (
            <div className="text-center py-16">
              <div className="mx-auto h-16 w-16 bg-gradient-to-br from-blue-100 to-blue-200 rounded-full flex items-center justify-center mb-4">
                <svg className="h-8 w-8 text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Universitet tapılmadı</h3>
              <p className="text-sm text-gray-500 max-w-sm mx-auto">
                Axtarış şərtlərinizi dəyişin və ya yeni universitet əlavə edin.
              </p>
              {canAddUniversity && (
                <button
                  onClick={handleAdd}
                  className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <PlusIcon className="h-4 w-4 mr-2" />
                  İlk Universiteti Əlavə Et
                </button>
              )}
            </div>
          )}

          {isLoading && (
            <div className="flex justify-center items-center py-16">
              <Audio
                height="80"
                width="80"
                radius="9"
                color="blue"
                ariaLabel="loading"
                wrapperStyle={{}}
                wrapperClass=""
              />
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <UniversityEditModal
          editingUniversity={editingUniversity}
          formData={formData}
          handleSubmit={handleSubmit}
          setFormData={setFormData}
          setIsModalOpen={setIsModalOpen}
          currenciesData={currenciesData}
          educationLevelsData={educationLevelsData}
        />
      )}

      {/* View Modal */}
      {isViewModalOpen && viewingUniversity && (
        <UniversityViewModal
          setIsViewModalOpen={setIsViewModalOpen}
          viewingUniversity={viewingUniversity}
          handleEdit={handleEdit}
        />
      )}

      {/* Country Creation Modal */}
      {showCountryModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75" onClick={handleCancelCountryModal} />
            
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="mb-4">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">
                    Yeni Ölkə Əlavə Et
                  </h3>
                  <button
                    onClick={handleCancelCountryModal}
                    className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <span className="text-xl">&times;</span>
                  </button>
                </div>
                <p className="mt-1 text-sm text-gray-600">
                  Sistem də olmayan yeni ölkə əlavə edin
                </p>
              </div>

              <form onSubmit={handleCountrySubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ölkə Adı *
                  </label>
                  <input
                    type="text"
                    required
                    value={newCountryData.name}
                    onChange={(e) => setNewCountryData({ ...newCountryData, name: e.target.value })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Məsələn: Almanya"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ölkə Kodu *
                  </label>
                  <input
                    type="text"
                    required
                    value={newCountryData.code}
                    onChange={(e) => setNewCountryData({ ...newCountryData, code: e.target.value.toUpperCase() })}
                    className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Məsələn: DE"
                    maxLength="3"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="mt-2">
                    <label className="inline-flex items-center">
                      <input
                        type="checkbox"
                        checked={newCountryData.is_active}
                        onChange={(e) => setNewCountryData({ ...newCountryData, is_active: e.target.checked ? 1 : 0 })}
                        className="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">
                        Ölkə aktiv statusdadır
                      </span>
                    </label>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={handleCancelCountryModal}
                    className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    Ləğv et
                  </button>
                  <button
                    type="submit"
                    disabled={countryCreateIsLoading}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {countryCreateIsLoading ? 'Əlavə edilir...' : 'Yadda saxla'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 